import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Smartphone } from 'lucide-react';
import { API, useAuth } from '../App';

const CheckoutPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [shippingAddress, setShippingAddress] = useState(user?.address || '');

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      if (response.data.items.length === 0) {
        navigate('/cart');
      }
    } catch (error) {
      console.error('Failed to fetch cart', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const subtotal = calculateTotal();
  const gstAmount = subtotal * 0.18;
  const shippingCharges = cart.items.reduce((sum, item) => sum + (500 * item.quantity), 0);
  const grandTotal = subtotal + gstAmount + shippingCharges;

  const handleCheckout = async () => {
    if (!shippingAddress.trim()) {
      toast.error('Please enter a shipping address');
      return;
    }

    setProcessing(true);

    try {
      const orderData = {
        items: cart.items,
        payment_method: paymentMethod,
        shipping_address: shippingAddress,
        gst_amount: gstAmount,
        shipping_charges: shippingCharges
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const order = orderResponse.data;

      if (paymentMethod === 'upi') {
        toast.success('Order placed! Please complete UPI payment by scanning the QR code.');
        navigate('/dashboard');
      } else if (paymentMethod === 'invoice') {
        toast.success('Order placed successfully! Invoice will be sent to your email.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Checkout failed', error);
      toast.error('Checkout failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <h1 className="text-4xl md:text-5xl font-heading font-bold mb-12">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Address */}
            <div className="bg-white border border-border p-6">
              <h2 className="text-2xl font-heading font-semibold mb-6">Shipping Address</h2>
              <textarea
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                rows="4"
                placeholder="Enter your complete shipping address"
                data-testid="shipping-address-input"
              />
            </div>

            {/* Payment Method */}
            <div className="bg-white border border-border p-6">
              <h2 className="text-2xl font-heading font-semibold mb-6">Payment Method</h2>
              
              <div className="space-y-4">
                {/* UPI Payment */}
                <div
                  onClick={() => setPaymentMethod('upi')}
                  className={`p-4 border-2 cursor-pointer transition-all duration-300 flex items-center space-x-4 ${
                    paymentMethod === 'upi'
                      ? 'border-primary bg-accent'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="payment-method-upi"
                >
                  <Smartphone size={24} className="text-primary" />
                  <div>
                    <p className="font-semibold">UPI Payment (Scan & Pay)</p>
                    <p className="text-sm text-muted-foreground">Pay instantly via any UPI app - GPay, PhonePe, Paytm etc.</p>
                  </div>
                </div>

                {/* UPI QR Code Display */}
                {paymentMethod === 'upi' && (
                  <div className="border border-border bg-white p-6 text-center" data-testid="upi-qr-section">
                    <p className="text-lg font-semibold mb-2">Scan QR Code to Pay</p>
                    <p className="text-primary font-bold text-2xl mb-4">
                      &#8377;{grandTotal.toFixed(2)}
                    </p>
                    <div className="inline-block bg-white p-3 border border-border shadow-sm">
                      <img
                        src="/upi-qr.jpg"
                        alt="UPI QR Code"
                        className="w-56 h-56 object-contain mx-auto"
                        data-testid="upi-qr-image"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      Scan with any UPI app (GPay, PhonePe, Paytm, etc.)
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      After payment, click "Place Order" below. Your order will be confirmed once payment is verified.
                    </p>
                  </div>
                )}

                {/* Invoice */}
                <div
                  onClick={() => setPaymentMethod('invoice')}
                  className={`p-4 border-2 cursor-pointer transition-all duration-300 flex items-center space-x-4 ${
                    paymentMethod === 'invoice'
                      ? 'border-primary bg-accent'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="payment-method-invoice"
                >
                  <FileText size={24} className="text-primary" />
                  <div>
                    <p className="font-semibold">Invoice / Credit Payment</p>
                    <p className="text-sm text-muted-foreground">Pay via invoice (B2B customers)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border p-6 sticky top-24">
              <h2 className="text-2xl font-heading font-semibold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.product_name} x {item.quantity}
                    </span>
                    <span className="font-semibold">&#8377;{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-semibold">&#8377;{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="font-semibold">&#8377;{gstAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping (&#8377;500/pc)</span>
                    <span className="font-semibold">&#8377;{shippingCharges.toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Grand Total</span>
                    <span className="font-bold text-luxury-accentGold" data-testid="checkout-total">&#8377;{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={processing}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-order-button"
              >
                {processing ? 'Processing...' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
